import React, { useState, useEffect } from 'react';
import { useCart } from '../components/CartContext';
import {
    Container,
    Typography,
    Grid,
    Button,
    TextField,
    Dialog,
    DialogContent,
    Box,
    CircularProgress,
    Alert,
    MenuItem,
    Paper,
    Divider,
    Collapse,
    IconButton,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Link,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import LoginRegisterComponent from '../components/LoginRegister';

const Checkout = () => {
    const { cart, clearCart } = useCart();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        businessName: '',
    });

    const [showLoginRegister, setShowLoginRegister] = useState(false);
    // True when the LoginRegister modal is being shown because the backend told
    // us an account already exists for the checkout email — we open it on the
    // Login tab instead of Register so the user isn't told "email already in use".
    const [emailExistsLogin, setEmailExistsLogin] = useState(false);
    const [error, setError] = useState('');
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showPayPalButtons, setShowPayPalButtons] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);

    // Coupon states
    const [showCouponForm, setShowCouponForm] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState('');
    const [couponSuccess, setCouponSuccess] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    // Acknowledgment state for No Pass No Pay policy
    const [guaranteeAcknowledged, setGuaranteeAcknowledged] = useState(false);

    // Fulfillment states
    const [fulfillmentType, setFulfillmentType] = useState('shipping');
    const [pickupDetails, setPickupDetails] = useState({
        scheduledDate: null,
        scheduledTime: '',
        notes: '',
    });

    // Check if cart contains physical items (books)
    const hasPhysicalItems = () => {
        if (!cart || cart.length === 0) return false;
        return cart.some(item =>
            item.selectedOption === 'studyProgramWithBooks' ||
            item.selectedOption === 'booksOnly' ||
            item.selectedOption === 'studyProgramAndRentBooks'
        );
    };

    useEffect(() => {
        checkAuthStatus();
    }, []);

    useEffect(() => {
        // All orders require full address (for billing and tracking purposes)
        const requiredFields = ['firstName', 'lastName', 'email', 'phoneNumber', 'addressLine1', 'city', 'state', 'zip'];

        const isComplete = requiredFields.every((field) => formData[field] && formData[field].trim() !== '');
        setIsFormComplete(isComplete);
    }, [formData, fulfillmentType, cart]);

    const US_STATES = [
        { value: 'AL', label: 'Alabama' },
        { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' },
        { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' },
        { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' },
        { value: 'DE', label: 'Delaware' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' },
        { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' },
        { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' },
        { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' },
        { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' },
        { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' },
        { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' },
        { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' },
        { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' },
        { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' },
        { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' },
        { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' },
        { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' },
        { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' },
        { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' },
        { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' },
        { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' },
        { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' },
        { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' },
        { value: 'WY', label: 'Wyoming' },
    ];

    const checkAuthStatus = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                console.log('No auth token found');
                setUser(null);
                setLoading(false);
                return;
            }
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Set user data
            const userData = response.data.data;
            setUser(userData);

            // Pre-populate form with user data
            updateFormWithUserData(userData);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching user data:', error);
            localStorage.removeItem('token');
            setUser(null);
            setLoading(false);
        }
    };

    const validateField = (name, value) => {
        let error = '';
        switch (name) {
            case 'firstName':
            case 'lastName':
                if (!value.trim()) error = 'This field is required';
                else if (!/^[a-zA-Z]+$/.test(value)) error = 'Only letters are allowed';
                break;
            case 'email':
                if (!value.trim()) error = 'This field is required';
                else if (!/\S+@\S+\.\S+/.test(value)) error = 'Invalid email';
                break;
            case 'phoneNumber':
                if (!value.trim()) error = 'This field is required';
                else if (!/^\d{10}$/.test(value.replace(/\D/g, ''))) error = 'Invalid phone number';
                break;
            case 'addressLine1':
            case 'city':
            case 'state':
                if (!value.trim()) error = 'This field is required';
                break;
            case 'zip':
                if (!value.trim()) error = 'This field is required';
                else if (!/^\d{5}(-\d{4})?$/.test(value.trim())) error = 'Invalid ZIP code (use 12345 or 12345-6789 format)';
                break;
            default:
                break;
        }
        return error;
    };

    const updateFormWithUserData = (userData) => {
        const newFormData = {
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            phoneNumber: userData.phone || '', // Map phone to phoneNumber
            addressLine1: (userData.address || '').trim(), // Trim address fields
            city: (userData.city || '').trim(),
            state: (userData.state || '').trim(),
            zip: (userData.zip || '').trim(),
        };

        setFormData((prevState) => ({
            ...prevState,
            ...newFormData,
        }));

        // Calculate tax if state is provided
        if (newFormData.state) {
            setTimeout(() => calculateTaxForState(newFormData.state), 100);
        }

        // Calculate shipping if address is complete
        if (newFormData.zip && newFormData.city) {
            setTimeout(() => calculateShipping(), 200);
        }
    };

    const validateAndCorrectAddress = async () => {
        if (!formData.addressLine1 || !formData.city || !formData.state || !formData.zip) {
            setVerificationMessage('Please complete all address fields for verification');
            return false;
        }

        setVerificationMessage('Verifying and standardizing address...');
        setAddressVerified(false);

        try {
            const token = localStorage.getItem('token');

            // Make request with or without token - API now accepts both
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/public/validate-address`,
                {
                    street: formData.addressLine1.trim(),
                    city: formData.city.trim(),
                    state: formData.state.trim(),
                    zipCode: formData.zip.trim(),
                },
                { headers },
            );

            if (response.data.status === 'success') {
                if (response.data.data.valid && response.data.data.addresses?.length > 0) {
                    const verified = response.data.data.addresses[0];
                    let updates = [];

                    // Auto-fill/correct all address components
                    const newFormData = { ...formData };

                    // Update street address if standardized differently
                    if (verified.street && verified.street !== formData.addressLine1) {
                        newFormData.addressLine1 = verified.street;
                        updates.push(`Street: ${verified.street}`);
                    }

                    // Update city if different
                    if (verified.city && verified.city !== formData.city) {
                        newFormData.city = verified.city;
                        updates.push(`City: ${verified.city}`);
                    }

                    // Update state if needed (handles full state names)
                    if (verified.state && verified.state !== formData.state) {
                        newFormData.state = verified.state;
                        updates.push(`State: ${verified.state}`);
                    }

                    // Update ZIP to ZIP+4 if available
                    if (verified.zip && verified.zip !== formData.zip) {
                        newFormData.zip = verified.zip;
                        updates.push(`ZIP: ${verified.zip}`);
                    }

                    // Apply all updates at once
                    setFormData(newFormData);

                    if (updates.length > 0) {
                        setVerificationMessage(`✓ Address standardized and verified! Updated: ${updates.join(', ')}`);
                    } else {
                        setVerificationMessage('✓ Address verified - no corrections needed!');
                    }

                    setAddressVerified(true);

                    // Calculate tax and shipping with verified address
                    setTimeout(() => {
                        calculateTaxForState(newFormData.state);
                        calculateShipping();
                    }, 100);

                    return true;
                } else {
                    // Address not found - offer to proceed anyway
                    setVerificationMessage(
                        '⚠️ Address could not be verified. Please double-check your address. ' +
                            'We are not responsible for packages sent to incorrect addresses. ' +
                            'Undeliverable items may incur additional shipping charges.',
                    );
                    setAddressVerified(false);

                    // Still try to calculate tax
                    calculateTaxForState(formData.state);
                    return false;
                }
            }
        } catch (error) {
            console.error('Address validation error:', error);
            setVerificationMessage('Unable to verify address at this time. You can still proceed.');
            // Still calculate tax even if verification fails
            calculateTaxForState(formData.state);
            return false;
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Don't trim on change - let users type spaces naturally
        // Trimming will happen during validation and submission
        setFormData({ ...formData, [name]: value });

        // Validate with trimmed value for validation purposes only
        const trimmedForValidation = ['zip', 'city', 'state', 'addressLine1'].includes(name) ? value.trim() : value;
        setFieldErrors({ ...fieldErrors, [name]: validateField(name, trimmedForValidation) });

        // Recalculate tax when state changes
        if (name === 'state') {
            calculateTaxForState(value.trim());
        }

        // Auto-verify address when ZIP code is complete (5 digits)
        if (name === 'zip' && value.trim().length >= 5) {
            // Check if we have all required fields for verification
            const updatedForm = { ...formData, [name]: value };
            if (updatedForm.addressLine1 && updatedForm.city && updatedForm.state) {
                setTimeout(() => {
                    validateAndCorrectAddress();
                }, 800); // Delay to allow user to finish typing
            }
        }

        // Also auto-verify when user completes city (and other fields are filled)
        if (name === 'city' && value.trim().length > 2) {
            const updatedForm = { ...formData, [name]: value };
            if (updatedForm.addressLine1 && updatedForm.state && updatedForm.zip && updatedForm.zip.trim().length >= 5) {
                setTimeout(() => {
                    validateAndCorrectAddress();
                }, 1000); // Slightly longer delay for city typing
            }
        }

        // Clear verification if user changes address after verification
        if (addressVerified && (name === 'addressLine1' || name === 'city' || name === 'state' || name === 'zip')) {
            setAddressVerified(false);
            setVerificationMessage('Address changed - please re-verify');
        }
    };

    const [addressVerified, setAddressVerified] = useState(false);
    const [verificationMessage, setVerificationMessage] = useState('');

    const calculateTaxForState = async (stateCode) => {
        if (!stateCode) {
            setTaxRate(0);
            setTaxAmount(0);
            setTaxBreakdown(null);
            return;
        }

        setTaxLoading(true);

        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            // Call TaxJar API for accurate tax calculation
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/tax/calculate`,
                {
                    cart: cart.map((item) => ({
                        productId: item._id,
                        selectedOption: item.selectedOption || 'studyProgramOnly',
                        quantity: item.quantity,
                    })),
                    shippingAddress: {
                        street: formData.addressLine1 || '',
                        city: formData.city || '',
                        state: stateCode,
                        zip: formData.zip || '',
                        country: 'US',
                    },
                    couponDiscount: appliedCoupon ? appliedCoupon.discountAmount : 0,
                },
                { headers },
            );

            if (response.data.status === 'success') {
                const { taxAmount: calculatedTax, taxRate: rate, hasNexus, breakdown } = response.data.data;

                if (!hasNexus) {
                    // No tax nexus in this state
                    setTaxAmount(0);
                    setTaxRate(0);
                    setTaxBreakdown(null);
                    console.log('No tax nexus in state:', stateCode);
                } else {
                    setTaxAmount(calculatedTax || 0);
                    setTaxRate(rate || 0);
                    setTaxBreakdown(breakdown || null);

                    // Log breakdown for debugging
                    if (breakdown) {
                        console.log('Tax breakdown received:', breakdown);
                        console.log('Tax breakdown percentages:', {
                            state: `${(breakdown.stateTax * 100).toFixed(2)}%`,
                            county: `${(breakdown.countyTax * 100).toFixed(2)}%`,
                            city: `${(breakdown.cityTax * 100).toFixed(2)}%`,
                            special: `${(breakdown.specialDistrictTax * 100).toFixed(2)}%`,
                            combined: `${(breakdown.combinedRate * 100).toFixed(2)}%`,
                        });
                        console.log('Current state:', formData.state, 'Is LA?', formData.state === 'LA');
                    } else {
                        console.log('No tax breakdown received');
                    }
                }
            }
        } catch (error) {
            console.error('TaxJar calculation error:', error);

            // Try to get rates directly if calculation fails
            if (formData.zip) {
                try {
                    const ratesResponse = await axios.get(
                        `${process.env.REACT_APP_API_URL}/api/tax/rates/${formData.zip}?state=${stateCode}&city=${formData.city || ''}`,
                    );

                    if (ratesResponse.data.status === 'success') {
                        const rate = ratesResponse.data.data.combinedRate || 0;
                        setTaxRate(rate);

                        // Calculate tax on taxable items
                        const taxableSubtotal = cart
                            ? cart.reduce((total, product) => {
                                  const isTaxable =
                                      product.selectedOption === 'studyProgramWithBooks' ||
                                      product.selectedOption === 'studyProgramAndRentBooks';
                                  return total + (isTaxable ? product.price * product.quantity : 0);
                              }, 0)
                            : 0;

                        setTaxAmount(taxableSubtotal * rate);
                        setTaxBreakdown(null); // Clear breakdown when using fallback
                    }
                } catch (ratesError) {
                    console.error('Rates lookup failed:', ratesError);
                    // Final fallback to hardcoded rates
                    const stateTaxRate = STATE_TAX_RATES[stateCode] || 0;
                    setTaxRate(stateTaxRate);
                    setTaxBreakdown(null); // Clear breakdown for fallback
                    const taxableSubtotal = cart
                        ? cart.reduce((total, product) => {
                              const isTaxable = product.selectedOption === 'studyProgramWithBooks';
                              return total + (isTaxable ? product.price * product.quantity : 0);
                          }, 0)
                        : 0;
                    setTaxAmount(taxableSubtotal * stateTaxRate);
                }
            }
        }

        setTaxLoading(false);
    };

    const validateForm = () => {
        const errors = {};
        Object.keys(formData).forEach((key) => {
            const error = validateField(key, formData[key]);
            if (error) errors[key] = error;
        });
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const [taxAmount, setTaxAmount] = useState(0);
    const [taxLoading, setTaxLoading] = useState(false);
    const [shippingCost, setShippingCost] = useState(0);
    const [shippingLoading, setShippingLoading] = useState(false);
    const [taxRate, setTaxRate] = useState(0);
    const [taxBreakdown, setTaxBreakdown] = useState(null);

    // State tax rates (2024 rates - approximate)
    const STATE_TAX_RATES = {
        AL: 0.04, // Alabama - 4%
        AK: 0.0, // Alaska - 0%
        AZ: 0.056, // Arizona - 5.6%
        AR: 0.065, // Arkansas - 6.5%
        CA: 0.0725, // California - 7.25%
        CO: 0.029, // Colorado - 2.9%
        CT: 0.0635, // Connecticut - 6.35%
        DE: 0.0, // Delaware - 0%
        FL: 0.06, // Florida - 6%
        GA: 0.04, // Georgia - 4%
        HI: 0.04, // Hawaii - 4%
        ID: 0.06, // Idaho - 6%
        IL: 0.0625, // Illinois - 6.25%
        IN: 0.07, // Indiana - 7%
        IA: 0.06, // Iowa - 6%
        KS: 0.065, // Kansas - 6.5%
        KY: 0.06, // Kentucky - 6%
        LA: 0.09, // Louisiana - 9% (state rate, parishes may vary)
        ME: 0.055, // Maine - 5.5%
        MD: 0.06, // Maryland - 6%
        MA: 0.0625, // Massachusetts - 6.25%
        MI: 0.06, // Michigan - 6%
        MN: 0.06875, // Minnesota - 6.875%
        MS: 0.07, // Mississippi - 7%
        MO: 0.04225, // Missouri - 4.225%
        MT: 0.0, // Montana - 0%
        NE: 0.055, // Nebraska - 5.5%
        NV: 0.0685, // Nevada - 6.85%
        NH: 0.0, // New Hampshire - 0%
        NJ: 0.06625, // New Jersey - 6.625%
        NM: 0.05125, // New Mexico - 5.125%
        NY: 0.04, // New York - 4%
        NC: 0.0475, // North Carolina - 4.75%
        ND: 0.05, // North Dakota - 5%
        OH: 0.0575, // Ohio - 5.75%
        OK: 0.045, // Oklahoma - 4.5%
        OR: 0.0, // Oregon - 0%
        PA: 0.06, // Pennsylvania - 6%
        RI: 0.07, // Rhode Island - 7%
        SC: 0.06, // South Carolina - 6%
        SD: 0.045, // South Dakota - 4.5%
        TN: 0.07, // Tennessee - 7%
        TX: 0.0625, // Texas - 6.25%
        UT: 0.0485, // Utah - 4.85%
        VT: 0.06, // Vermont - 6%
        VA: 0.043, // Virginia - 4.3%
        WA: 0.065, // Washington - 6.5%
        WV: 0.06, // West Virginia - 6%
        WI: 0.05, // Wisconsin - 5%
        WY: 0.04, // Wyoming - 4%
    };

    const calculateSubtotal = () => {
        return cart ? cart.reduce((total, product) => total + product.price * product.quantity, 0) : 0;
    };

    // Calculate tax when state or cart changes
    useEffect(() => {
        if (formData.state && cart && cart.length > 0) {
            calculateTaxForState(formData.state);
            // Still calculate shipping if needed
            if (formData.zip && formData.city) {
                calculateShipping();
            }
        } else {
            setTaxAmount(0);
        }
    }, [formData.state, cart]);

    // Recalculate shipping when fulfillment type changes
    useEffect(() => {
        if (cart && cart.length > 0) {
            calculateShipping();
        }
    }, [fulfillmentType]);

    // Calculate shipping based on physical items and actual weight
    const calculateShipping = async () => {
        setShippingLoading(true);

        // If pickup is selected, no shipping cost
        if (fulfillmentType === 'pickup') {
            setShippingCost(0);
            setShippingLoading(false);
            return;
        }

        // Check if any items need shipping (physical books)
        const needsShipping =
            cart &&
            cart.some((item) => item.selectedOption === 'studyProgramWithBooks' || item.selectedOption === 'studyProgramAndRentBooks');

        if (!needsShipping || !formData.zip || !formData.city || !formData.state) {
            // Only add handling fee if there are physical items that need shipping
            setShippingCost(needsShipping ? 10.27 : 0);
            setShippingLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            // If incomplete address, use weight-based estimate
            if (!formData.addressLine1) {
                // Calculate based on total weight
                const totalWeight = cart.reduce((weight, item) => {
                    if (item.selectedOption === 'studyProgramWithBooks') {
                        // Use actual product weight if available
                        const productWeight = item.bookPackageInfo?.weight || 10;
                        return weight + productWeight * item.quantity;
                    }
                    return weight;
                }, 0);

                // USPS Media Mail estimate: ~$3.50 for first pound, $0.60 per additional pound + handling
                const estimatedShipping = totalWeight > 0 ? 3.5 + (totalWeight - 1) * 0.6 : 0;
                setShippingCost(Math.min(estimatedShipping, 25) + 10.27); // Add $10.27 handling fee
                setShippingLoading(false);
                return;
            }

            // Call actual shipping API for accurate rates
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/public/calculate-shipping`,
                {
                    cart: cart.map((item) => ({
                        productId: item._id,
                        selectedOption: item.selectedOption || 'studyProgramOnly',
                        quantity: item.quantity,
                    })),
                    shippingAddress: {
                        name: `${formData.firstName} ${formData.lastName}`,
                        street: formData.addressLine1,
                        city: formData.city,
                        state: formData.state,
                        zip: formData.zip,
                        country: 'US',
                    },
                },
                { headers },
            );

            if (response.data.status === 'success' && response.data.data?.rates?.length > 0) {
                // Use the cheapest rate (should be USPS Media Mail)
                const cheapestRate = response.data.data.rates.reduce((min, rate) =>
                    rate.shippingAmount < min.shippingAmount ? rate : min,
                );
                setShippingCost((cheapestRate.shippingAmount || 11.22) + 10.27); // Add $10.27 handling fee
            } else {
                // Fallback to weight-based calculation
                const totalWeight = cart.reduce((weight, item) => {
                    if (item.selectedOption === 'studyProgramWithBooks') {
                        const productWeight = item.bookPackageInfo?.weight || 10;
                        return weight + productWeight * item.quantity;
                    }
                    return weight;
                }, 0);
                const estimatedShipping = totalWeight > 0 ? 3.5 + (totalWeight - 1) * 0.6 : 0;
                setShippingCost(Math.min(estimatedShipping, 25) + 10.27); // Add $10.27 handling fee
            }
        } catch (error) {
            console.error('Shipping calculation error:', error);
            // Fallback to simple calculation
            const bookPackages = cart.filter((item) => item.selectedOption === 'studyProgramWithBooks').length;
            setShippingCost(8 + bookPackages * 3 + 10.27); // Add $10.27 handling fee
        }

        setShippingLoading(false);
    };

    const calculateCheckoutTotals = async () => {
        // Use local calculation for immediate feedback
        if (formData.state) {
            calculateTaxForState(formData.state);
        }
        if (formData.zip && formData.city) {
            calculateShipping();
        }

        // Optionally still call API for server-side validation
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/public/calculate-totals`,
                {
                    cart: cart.map((item) => ({
                        productId: item._id,
                        selectedOption: item.selectedOption || 'studyProgramOnly',
                        quantity: item.quantity,
                    })),
                    shippingAddress: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        street: formData.addressLine1,
                        addressLine1: formData.addressLine1,
                        addressLine2: formData.addressLine2,
                        city: formData.city,
                        state: formData.state,
                        zipCode: formData.zip,
                        zip: formData.zip,
                        phone: formData.phoneNumber,
                        phoneNumber: formData.phoneNumber,
                        country: 'US',
                    },
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            // Only use server response if significantly different (for validation)
            if (response.data.status === 'success') {
                const serverTax = response.data.data.tax?.taxAmount || 0;
                const serverShipping = response.data.data.shipping?.totalShippingCost || 0;

                // Use server values only if they differ significantly (more than $1)
                if (Math.abs(serverTax - taxAmount) > 1) {
                    setTaxAmount(serverTax);
                }
                if (Math.abs(serverShipping - shippingCost) > 1) {
                    setShippingCost(serverShipping);
                }
            }
        } catch (error) {
            // Silently fail - we already have local calculations
            console.log('Server calculation unavailable, using local calculation');
        }
    };

    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        let total = subtotal;

        // Apply coupon discount if available
        if (appliedCoupon) {
            total = Math.max(0, subtotal - appliedCoupon.discountAmount);
        }

        // Add tax and shipping
        total += taxAmount + shippingCost;

        return total;
    };

    // Handle coupon application
    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) {
            setCouponError('Please enter a coupon code');
            return;
        }

        try {
            setCouponLoading(true);
            setCouponError('');
            setCouponSuccess('');

            const token = localStorage.getItem('token');
            if (!token) {
                setCouponError('You must be logged in to apply a coupon');
                setShowLoginRegister(true);
                return;
            }

            // Validate the coupon with the API
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/coupons/validate`,
                {
                    code: couponCode,
                    cartItems: cart,
                    total: calculateSubtotal(),
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            // If valid, apply the coupon
            if (response.data.status === 'success') {
                const couponData = response.data.data.coupon;
                setAppliedCoupon(couponData);
                setCouponSuccess(
                    `Coupon applied: ${
                        couponData.type === 'percentage' ? `${couponData.value}% off` : `$${couponData.value.toFixed(2)} off`
                    }`,
                );
                setCouponCode('');
            }
        } catch (error) {
            console.error('Error applying coupon:', error);
            setCouponError(error.response?.data?.message || 'Failed to apply coupon');
            setAppliedCoupon(null);
        } finally {
            setCouponLoading(false);
        }
    };

    // Remove applied coupon
    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponSuccess('');
        setCouponError('');
    };

    const handleLoginSuccess = (userData) => {
        setUser(userData);
        setShowLoginRegister(false);
        setEmailExistsLogin(false);
        localStorage.setItem('token', userData.token);
        localStorage.setItem('user', JSON.stringify(userData));
        updateFormWithUserData(userData);
        setShowPayPalButtons(true);
    };

    // Builds the exact payload POSTed to /api/orders. Also used to snapshot the
    // checkout server-side BEFORE PayPal captures, so the backend can recover
    // the order if this browser never finishes (lost-order incident, June 2026).
    const buildOrderData = (paypalOrderId) => {
        // Log cart contents for debugging
        console.log('Cart contents before checkout:', JSON.stringify(cart, null, 2));

        // Ensure cart items have _id property
        const validatedCart = cart
            .map((item) => {
                // Make a copy to avoid modifying the original cart
                const cartItem = { ...item };
                // If productId exists but _id doesn't, use productId as _id
                if (!cartItem._id && cartItem.productId) {
                    console.log('Using productId as _id for cart item:', cartItem.name);
                    cartItem._id = cartItem.productId;
                }

                if (!cartItem._id) {
                    console.error('Cart item missing _id:', cartItem);
                    return null;
                }
                return cartItem;
            })
            .filter((item) => item !== null);

        if (validatedCart.length !== cart.length) {
            console.warn(`Filtered out ${cart.length - validatedCart.length} invalid cart items`);
        }

        const orderData = {
            cart: validatedCart.map((item) => ({
                ...item,
                selectedOption: item.selectedOption || 'studyProgramOnly',
            })),
            total: calculateTotal(),
            subtotal: calculateSubtotal(),
            tax: {
                amount: taxAmount,
            },
            shipping: {
                shippingCost: fulfillmentType === 'pickup' ? 0 : shippingCost,
                returnShippingCost: 0, // Will be calculated by backend
                totalShippingCost: fulfillmentType === 'pickup' ? 0 : shippingCost,
            },
            paypalOrderId,
            fulfillmentType: fulfillmentType,
            pickupDetails: fulfillmentType === 'pickup' ? pickupDetails : undefined,
            userInfo: {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: formData.phoneNumber.trim(), // Map phoneNumber to phone
                address: formData.addressLine1.trim(), // Map addressLine1 to address and trim
                addressLine2: formData.addressLine2?.trim() || '',
                city: formData.city.trim(),
                state: formData.state.trim(),
                zip: formData.zip.trim(),
                businessName: formData.businessName?.trim() || '',
            },
        };

        // Add coupon information if a coupon is applied
        if (appliedCoupon) {
            orderData.coupon = appliedCoupon;
        }

        return orderData;
    };

    // Saves the checkout snapshot server-side before capture. Best-effort: a
    // failure here must never block the payment, it only weakens the backstop.
    const snapshotPendingCheckout = async (paypalOrderId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${process.env.REACT_APP_API_URL}/api/orders/pending`, buildOrderData(paypalOrderId), {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                timeout: 8000,
            });
        } catch (snapshotError) {
            console.warn('Pre-capture snapshot failed (non-fatal):', snapshotError);
        }
    };

    // POST the order with retries. Money is already captured by the time this
    // runs, so transient network/server hiccups must not lose the order.
    // Client errors (4xx, e.g. EMAIL_EXISTS) are not retried.
    const postOrderWithRetry = async (orderData, authHeaders) => {
        const maxAttempts = 3;
        let lastError;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await axios.post(`${process.env.REACT_APP_API_URL}/api/orders`, orderData, {
                    headers: authHeaders,
                });
            } catch (error) {
                lastError = error;
                const status = error.response?.status;
                if (status && status < 500) throw error; // real rejection — don't retry
                console.warn(`Order POST attempt ${attempt}/${maxAttempts} failed:`, error.message);
                if (attempt < maxAttempts) {
                    await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
                }
            }
        }
        throw lastError;
    };

    const createOrder = async (details) => {
        try {
            const token = localStorage.getItem('token');
            const orderData = buildOrderData(details.id);

            // Guest checkout is allowed — only send Authorization if we actually have a token.
            const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

            // No pre-verify round trip needed: the backend is idempotent on
            // paypalOrderId and returns the existing order as success if this
            // capture was already processed.
            const response = await postOrderWithRetry(orderData, authHeaders);

            if (response.data.status === 'success') {
                // If the backend auto-created an account for this checkout, log them in
                // using the JWT it returned so the confirmation page is authenticated.
                if (response.data.newAccountCreated && response.data.token) {
                    localStorage.setItem('token', response.data.token);
                }

                clearCart();

                // Navigate to order confirmation with order data
                navigate('/order-confirmation', {
                    state: {
                        orderData: response.data.data,
                        newAccountCreated: response.data.newAccountCreated || false,
                    },
                });
            } else {
                setError('Failed to create order. Please try again.');
            }
        } catch (error) {
            console.error('Error creating order:', error);
            // Backend signals an existing account for this email — switch the
            // LoginRegister modal to the Login tab with the email prefilled.
            if (error.response?.status === 409 && error.response?.data?.code === 'EMAIL_EXISTS') {
                setError('It looks like you already have an account. Please log in to complete your purchase — your cart will be preserved.');
                setShowPayPalButtons(false);
                setEmailExistsLogin(true);
                setShowLoginRegister(true);
                return;
            }
            // IMPORTANT: by the time createOrder runs, PayPal has ALREADY
            // captured the payment. Never tell the customer to "try again" —
            // they'd pay twice. The pre-capture snapshot + reconcile cron will
            // finish the order server-side automatically.
            setError(
                'Your payment was received, but we hit a temporary problem finalizing your order. ' +
                'It will be completed automatically within about 15 minutes and your confirmation ' +
                'email will follow. Please do NOT pay again. If you have not received an email ' +
                'within an hour, contact us at info@contractortestla.com.',
            );
        }
    };

    const handleProceedToPayment = () => {
        if (validateForm()) {
            console.log('PayPal Client ID:', process.env.REACT_APP_PAYPAL_CLIENT_ID);
            if (!process.env.REACT_APP_PAYPAL_CLIENT_ID) {
                setError('PayPal configuration error. Please contact support.');
                return;
            }
            // Guests are allowed: the backend will auto-create an account from
            // the form data and email login credentials after the order completes.
            setShowPayPalButtons(true);
        } else {
            setError('Please correct the errors in the form before proceeding.');
        }
    };

    if (loading) {
        return (
            <Container
                sx={{
                    mt: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                }}
            >
                <CircularProgress />
            </Container>
        );
    }

    if (!cart || cart.length === 0) {
        return (
            <Container sx={{ mt: 4 }}>
                <Typography variant="h5" align="center">
                    Your cart is empty. Please add items before proceeding to checkout.
                </Typography>
            </Container>
        );
    }

    return (
        <>
            <style jsx="true">{`
                @keyframes pulse {
                    0% {
                        transform: scale(1);
                        opacity: 0.5;
                    }
                    50% {
                        transform: scale(1.1);
                        opacity: 0.3;
                    }
                    100% {
                        transform: scale(1);
                        opacity: 0.5;
                    }
                }
                @keyframes bounceIn {
                    0% {
                        transform: scale(0.3);
                        opacity: 0;
                    }
                    50% {
                        transform: scale(1.05);
                    }
                    70% {
                        transform: scale(0.9);
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateX(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
            `}</style>
            <Container sx={{ mt: 4 }}>
                <Typography variant="h4" align="center" sx={{ mb: 4 }}>
                    Checkout
                </Typography>

                {/* Fulfillment Type Selection - Only show if cart has physical items */}
                {hasPhysicalItems() && (
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                        <ToggleButtonGroup
                            value={fulfillmentType}
                            exclusive
                            onChange={(e, newType) => {
                                if (newType !== null) {
                                    setFulfillmentType(newType);
                                }
                            }}
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 4,
                                    py: 2,
                                    borderRadius: 2,
                                    margin: '0 8px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    border: '2px solid',
                                    borderColor: 'divider',
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        borderColor: 'primary.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        },
                                    },
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                },
                            }}
                        >
                            <ToggleButton value="shipping">
                                <LocalShippingIcon sx={{ mr: 1 }} />
                                Ship to Address
                            </ToggleButton>
                            <ToggleButton value="pickup">
                                <StorefrontIcon sx={{ mr: 1 }} />
                                Local Pickup (Save on Shipping!)
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {/* Digital-only order notice */}
                {!hasPhysicalItems() && (
                    <Alert severity="info" sx={{ mb: 4 }}>
                        <Typography variant="body1" fontWeight="600">
                            Digital Access Only - No Shipping Required
                        </Typography>
                        <Typography variant="body2">
                            You're purchasing digital access only. Your account will be activated immediately after payment, and you'll receive a confirmation email with login instructions. We collect your billing address for payment verification and record-keeping purposes.
                        </Typography>
                    </Alert>
                )}

                {/* Pickup Location Info */}
                {fulfillmentType === 'pickup' && (
                    <Card
                        sx={{
                            mb: 3,
                            backgroundColor: '#f0f7ff',
                            border: '2px solid #1976d2',
                            animation: 'fadeIn 0.3s ease-in',
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                                <LocationOnIcon sx={{ mr: 1 }} />
                                Pickup Location
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Contractor Test LA Office
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                2834 S. Sherwood Forest Blvd., Suite D-2
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Baton Rouge, LA 70816
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Pickup Hours: 8:00 AM - 1:00 PM, Monday - Friday
                                </Typography>
                            </Box>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Please bring your order confirmation and a valid ID.
                            </Alert>
                        </CardContent>
                    </Card>
                )}

                <Box component="form" noValidate autoComplete="off">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                error={!!fieldErrors.firstName}
                                helperText={fieldErrors.firstName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                error={!!fieldErrors.lastName}
                                helperText={fieldErrors.lastName}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                error={!!fieldErrors.email}
                                helperText={fieldErrors.email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label="Phone Number"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                error={!!fieldErrors.phoneNumber}
                                helperText={fieldErrors.phoneNumber}
                            />
                        </Grid>
                        {/* Address Fields - Always required for billing and tracking */}
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label={
                                    !hasPhysicalItems()
                                        ? 'Address Line 1 (Billing)'
                                        : fulfillmentType === 'pickup'
                                            ? 'Address Line 1 (for records)'
                                            : 'Shipping Address Line 1'
                                }
                                name="addressLine1"
                                value={formData.addressLine1}
                                onChange={handleInputChange}
                                error={!!fieldErrors.addressLine1}
                                helperText={fieldErrors.addressLine1}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Line 2"
                                name="addressLine2"
                                value={formData.addressLine2}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label={!hasPhysicalItems() ? 'City (Billing)' : 'City'}
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                error={!!fieldErrors.city}
                                helperText={fieldErrors.city}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                required
                                fullWidth
                                label={!hasPhysicalItems() ? 'State (Billing)' : 'State'}
                                name="state"
                                value={formData.state}
                                onChange={handleInputChange}
                                error={!!fieldErrors.state}
                                helperText={fieldErrors.state}
                            >
                                {US_STATES.map((state) => (
                                    <MenuItem key={state.value} value={state.value}>
                                        {state.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label={!hasPhysicalItems() ? 'ZIP Code (Billing)' : 'ZIP Code'}
                                name="zip"
                                value={formData.zip}
                                onChange={handleInputChange}
                                error={!!fieldErrors.zip}
                                helperText={fieldErrors.zip}
                            />
                        </Grid>

                        {/* Info message for pickup orders */}
                        {fulfillmentType === 'pickup' && hasPhysicalItems() && (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Why we need your address for local pickup:</strong>
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        • For our records and order confirmation
                                    </Typography>
                                    <Typography variant="body2">• In case we need to ship your order if pickup isn't completed</Typography>
                                    <Typography variant="body2">• To verify your identity at pickup</Typography>
                                </Alert>
                            </Grid>
                        )}

                        {/* Beautiful Address Verification Section - Only for physical items */}
                        {hasPhysicalItems() && (
                            <Grid item xs={12}>
                                <Collapse in={formData.state && formData.city}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        background: addressVerified
                                            ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)'
                                            : 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        boxShadow: addressVerified
                                            ? '0 8px 32px rgba(76, 175, 80, 0.15)'
                                            : '0 8px 32px rgba(156, 39, 176, 0.1)',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        transform: addressVerified ? 'scale(1)' : 'scale(0.98)',
                                        '&:hover': {
                                            transform: 'scale(1)',
                                            boxShadow: addressVerified
                                                ? '0 12px 48px rgba(76, 175, 80, 0.2)'
                                                : '0 12px 48px rgba(156, 39, 176, 0.15)',
                                        },
                                    }}
                                >
                                    {/* Animated Background Pattern */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: -50,
                                            width: 200,
                                            height: 200,
                                            borderRadius: '50%',
                                            background: addressVerified
                                                ? 'radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 70%)'
                                                : 'radial-gradient(circle, rgba(156, 39, 176, 0.1) 0%, transparent 70%)',
                                            animation: 'pulse 3s ease-in-out infinite',
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            p: 3,
                                            position: 'relative',
                                            zIndex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'wrap',
                                                gap: 2,
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '50%',
                                                            background: addressVerified
                                                                ? 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)'
                                                                : 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                                                            boxShadow: addressVerified
                                                                ? '0 4px 12px rgba(76, 175, 80, 0.3)'
                                                                : '0 4px 12px rgba(156, 39, 176, 0.3)',
                                                            animation: addressVerified ? 'bounceIn 0.5s' : undefined,
                                                        }}
                                                    >
                                                        {addressVerified ? (
                                                            <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
                                                        ) : (
                                                            <LocationOnIcon sx={{ color: 'white', fontSize: 24 }} />
                                                        )}
                                                    </Box>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                            background: addressVerified
                                                                ? 'linear-gradient(45deg, #2E7D32, #43A047)'
                                                                : 'linear-gradient(45deg, #7B1FA2, #9C27B0)',
                                                            backgroundClip: 'text',
                                                            WebkitBackgroundClip: 'text',
                                                            WebkitTextFillColor: 'transparent',
                                                        }}
                                                    >
                                                        {addressVerified ? 'Address Verified!' : 'Verify Your Address'}
                                                    </Typography>
                                                </Box>

                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: verificationMessage?.includes('⚠️')
                                                            ? 'warning.main'
                                                            : addressVerified
                                                              ? 'success.dark'
                                                              : 'text.secondary',
                                                        mb: addressVerified || verificationMessage?.includes('⚠️') ? 1 : 0,
                                                        transition: 'all 0.3s',
                                                        fontWeight: verificationMessage?.includes('⚠️') ? 500 : 400,
                                                    }}
                                                >
                                                    {verificationMessage ||
                                                        (addressVerified
                                                            ? `📍 ${formData.addressLine1}, ${formData.city}, ${formData.state} ${formData.zip}`
                                                            : 'Ensure accurate delivery and tax calculation')}
                                                </Typography>

                                                {addressVerified && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 2,
                                                            flexWrap: 'wrap',
                                                            animation: 'fadeIn 0.5s',
                                                        }}
                                                    >
                                                        <Chip
                                                            icon={<CheckCircleIcon />}
                                                            label="USPS Validated"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'success.main',
                                                                color: 'white',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                        {formData.state === 'LA' && taxBreakdown?.countyTax > 0 && (
                                                            <Chip
                                                                label={`${formData.city} Parish Tax Applied`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'rgb(255, 94, 28)',
                                                                    color: 'white',
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>

                                            <Button
                                                variant="contained"
                                                onClick={validateAndCorrectAddress}
                                                disabled={!formData.addressLine1 || !formData.city || !formData.state || !formData.zip}
                                                startIcon={addressVerified ? <CheckCircleIcon /> : <LocationOnIcon />}
                                                size="large"
                                                sx={{
                                                    px: 3,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    background: addressVerified
                                                        ? 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)'
                                                        : 'linear-gradient(45deg, rgb(255, 94, 28) 30%, rgb(255, 140, 0) 90%)',
                                                    boxShadow: addressVerified
                                                        ? '0 6px 20px rgba(76, 175, 80, 0.4)'
                                                        : '0 6px 20px rgba(255, 94, 28, 0.4)',
                                                    transition: 'all 0.3s',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: addressVerified
                                                            ? '0 8px 28px rgba(76, 175, 80, 0.5)'
                                                            : '0 8px 28px rgba(156, 39, 176, 0.5)',
                                                    },
                                                }}
                                            >
                                                {addressVerified ? 'Verified ✓' : 'Verify Now'}
                                            </Button>
                                        </Box>

                                        {!addressVerified && formData.state && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    pt: 2,
                                                    borderTop: '1px solid rgba(156, 39, 176, 0.2)',
                                                    animation: 'slideIn 0.3s',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    <Box component="span" sx={{ fontSize: 18 }}>
                                                        ✨
                                                    </Box>
                                                    Verification auto-corrects typos and ensures fastest delivery
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Collapse>
                        </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Business Name (Optional)"
                                name="businessName"
                                value={formData.businessName}
                                onChange={handleInputChange}
                            />
                        </Grid>
                    </Grid>
                    {/* Coupon Section */}
                    <Box sx={{ mt: 4, mb: 2 }}>
                        <Paper elevation={1} sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocalOfferIcon sx={{ mr: 1, color: '#ff5e15' }} />
                                    Have a Coupon?
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setShowCouponForm(!showCouponForm)}
                                    disabled={!!appliedCoupon}
                                >
                                    {showCouponForm ? 'Hide' : 'Apply Coupon'}
                                </Button>
                            </Box>

                            <Collapse in={showCouponForm && !appliedCoupon}>
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}>
                                    <TextField
                                        variant="outlined"
                                        label="Coupon Code"
                                        size="small"
                                        fullWidth
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                        placeholder="Enter your coupon code"
                                        sx={{ mr: 1 }}
                                        disabled={couponLoading}
                                        error={!!couponError}
                                        helperText={couponError}
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleApplyCoupon}
                                        disabled={couponLoading || !couponCode.trim()}
                                    >
                                        {couponLoading ? <CircularProgress size={24} /> : 'Apply'}
                                    </Button>
                                </Box>
                            </Collapse>

                            {/* Display applied coupon */}
                            {appliedCoupon && (
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                Coupon: {appliedCoupon.code}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {appliedCoupon.type === 'percentage'
                                                    ? `${appliedCoupon.value}% off`
                                                    : `$${appliedCoupon.value.toFixed(2)} off`}
                                                {' - '}
                                                Discount: ${appliedCoupon.discountAmount.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton color="error" onClick={handleRemoveCoupon} size="small">
                                        <CancelIcon />
                                    </IconButton>
                                </Box>
                            )}

                            {couponSuccess && !couponError && (
                                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                    {couponSuccess}
                                </Typography>
                            )}
                        </Paper>
                    </Box>

                    {/* Order Summary */}
                    <Box sx={{ mt: 3, mb: 4 }}>
                        <Paper elevation={1} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Order Summary
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body1">Subtotal:</Typography>
                                <Typography variant="body1">${calculateSubtotal().toFixed(2)}</Typography>
                            </Box>

                            {appliedCoupon && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body1" color="error.main">
                                        Discount:
                                    </Typography>
                                    <Typography variant="body1" color="error.main">
                                        -${appliedCoupon.discountAmount.toFixed(2)}
                                    </Typography>
                                </Box>
                            )}

                            {/* Tax Display with Breakdown */}
                            {taxBreakdown && taxAmount > 0 ? (
                                <>
                                    {/* State Tax */}
                                    {taxBreakdown?.stateTax > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ pl: 2, color: 'text.secondary' }}>
                                                State Tax ({((taxBreakdown?.stateTax || 0) * 100).toFixed(2)}%):
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                $
                                                {(
                                                    (calculateSubtotal() - (appliedCoupon ? appliedCoupon.discountAmount : 0)) *
                                                    (taxBreakdown?.stateTax || 0)
                                                ).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Parish Tax for Louisiana */}
                                    {formData.state === 'LA' && taxBreakdown && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ pl: 2, color: 'text.secondary' }}>
                                                Parish Tax ({((taxBreakdown?.countyTax || 0) * 100).toFixed(2)}%):
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                $
                                                {(
                                                    (calculateSubtotal() - (appliedCoupon ? appliedCoupon.discountAmount : 0)) *
                                                    (taxBreakdown?.countyTax || 0)
                                                ).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* County Tax for other states */}
                                    {formData.state !== 'LA' && taxBreakdown?.countyTax > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ pl: 2, color: 'text.secondary' }}>
                                                County Tax ({((taxBreakdown?.countyTax || 0) * 100).toFixed(2)}%):
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                $
                                                {(
                                                    (calculateSubtotal() - (appliedCoupon ? appliedCoupon.discountAmount : 0)) *
                                                    (taxBreakdown?.countyTax || 0)
                                                ).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* City Tax */}
                                    {taxBreakdown?.cityTax > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ pl: 2, color: 'text.secondary' }}>
                                                City Tax ({((taxBreakdown?.cityTax || 0) * 100).toFixed(2)}%):
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                $
                                                {(
                                                    (calculateSubtotal() - (appliedCoupon ? appliedCoupon.discountAmount : 0)) *
                                                    (taxBreakdown?.cityTax || 0)
                                                ).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Special District Tax */}
                                    {taxBreakdown?.specialDistrictTax > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ pl: 2, color: 'text.secondary' }}>
                                                Special District Tax ({((taxBreakdown?.specialDistrictTax || 0) * 100).toFixed(2)}%):
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                $
                                                {(
                                                    (calculateSubtotal() - (appliedCoupon ? appliedCoupon.discountAmount : 0)) *
                                                    (taxBreakdown?.specialDistrictTax || 0)
                                                ).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Total Tax */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                            mt: 1,
                                            pt: 1,
                                            borderTop: '1px solid #e0e0e0',
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            Total Tax
                                            {taxLoading && ' (Calculating...)'}
                                            {formData.state && ` (${formData.state})`}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            ${taxAmount.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body1">
                                        Tax{' '}
                                        {taxLoading
                                            ? '(Calculating...)'
                                            : formData.state && taxRate > 0
                                              ? `(${formData.state} - ${(taxRate * 100).toFixed(2)}%)`
                                              : formData.state
                                                ? `(${formData.state})`
                                                : ''}
                                        :
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: taxAmount > 0 ? 'normal' : 300,
                                            color: taxLoading ? 'text.secondary' : 'inherit',
                                        }}
                                    >
                                        ${taxAmount.toFixed(2)}
                                    </Typography>
                                </Box>
                            )}

                            {/* Shipping or Pickup Display - Only for physical items */}
                            {hasPhysicalItems() && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body1">
                                        {fulfillmentType === 'pickup' ? (
                                            <>
                                                <StorefrontIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                                Local Pickup:
                                            </>
                                        ) : (
                                            <>
                                                Shipping (USPS Media Mail):
                                                {shippingLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
                                            </>
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: fulfillmentType === 'pickup' ? 'success.main' : 'inherit',
                                            fontWeight: fulfillmentType === 'pickup' ? 600 : 400,
                                        }}
                                    >
                                        {fulfillmentType === 'pickup' ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                                    </Typography>
                                </Box>
                            )}

                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6">Total:</Typography>
                                <Typography variant="h6" color="primary">
                                    ${calculateTotal().toFixed(2)}
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>

                    {/* No Pass No Pay Acknowledgment */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 2,
                            border: guaranteeAcknowledged ? '2px solid #4caf50' : '2px solid #ff9800',
                            bgcolor: guaranteeAcknowledged ? '#f1f8e9' : '#fff8e1',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#e65100' }}>
                            Important: No Pass, No Pay Guarantee Terms
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Please review and acknowledge the following before completing your purchase:
                        </Typography>
                        <Box sx={{
                            bgcolor: 'white',
                            p: 2,
                            borderRadius: 1,
                            mb: 2,
                            border: '1px solid #e0e0e0'
                        }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Money Back Guarantee Eligibility:</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, mb: 1 }}>
                                <li>The No Pass, No Pay guarantee applies <strong>only</strong> to the <strong>Complete Package</strong> (Study Program + Books bundle).</li>
                                <li>Refunds are limited to the prep course fee ($475). Book costs are non-refundable.</li>
                                <li>You must achieve at least 90% on practice exams and meet all eligibility requirements.</li>
                                <li>Claims must be submitted within 5 days of receiving your official exam failure notice.</li>
                            </Typography>
                            <Link
                                component={RouterLink}
                                to="/guarantee"
                                target="_blank"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mt: 1
                                }}
                            >
                                View Full Guarantee Details
                            </Link>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={guaranteeAcknowledged}
                                    onChange={(e) => setGuaranteeAcknowledged(e.target.checked)}
                                    sx={{
                                        color: '#ff5e1c',
                                        '&.Mui-checked': {
                                            color: '#4caf50',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" fontWeight="500">
                                    I have read and understand the No Pass, No Pay guarantee terms and conditions
                                </Typography>
                            }
                            sx={{
                                alignItems: 'flex-start',
                                '& .MuiFormControlLabel-label': {
                                    mt: 0.5
                                }
                            }}
                        />
                    </Paper>

                    {isFormComplete && guaranteeAcknowledged ? (
                        showPayPalButtons ? (
                            <PayPalScriptProvider
                                options={{
                                    'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
                                    currency: 'USD',
                                    intent: 'capture',
                                    components: 'buttons',
                                    'enable-funding': 'venmo,paylater,credit,card',
                                    'data-sdk-integration-source': 'react-paypal-js',
                                }}
                                onError={(err) => {
                                    console.error('PayPal Script Error:', err);
                                    setError('Failed to load PayPal. Please refresh and try again.');
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Box sx={{ width: '440px' }}>
                                        <PayPalButtons
                                            disabled={isProcessingPayment}
                                            fundingSource={undefined}
                                            forceReRender={[taxAmount, shippingCost, appliedCoupon]}
                                            onClick={(data, actions) => {
                                                // Validate before opening PayPal
                                                console.log('PayPal button clicked, validating...');
                                                const total = calculateTotal();
                                                if (total <= 0) {
                                                    setError('Invalid order total. Please check your cart.');
                                                    return actions.reject();
                                                }
                                                if (!cart || cart.length === 0) {
                                                    setError('Your cart is empty.');
                                                    return actions.reject();
                                                }
                                                return actions.resolve();
                                            }}
                                            createOrder={(data, actions) => {
                                                try {
                                                    // Create product description for PayPal
                                                    const productNames = cart.map((item) => item.name).join(', ');
                                                    const itemDescription =
                                                        productNames.length > 100 ? productNames.substring(0, 97) + '...' : productNames;

                                                    // Create proper PayPal breakdown with discount
                                                    const total = calculateTotal();
                                                    const subtotal = calculateSubtotal();
                                                    const discount = appliedCoupon ? appliedCoupon.discountAmount : 0;

                                                    // Ensure all values are numbers and properly formatted
                                                    const orderTotal = parseFloat(total.toFixed(2));
                                                    const orderSubtotal = parseFloat(subtotal.toFixed(2));
                                                    const orderTax = parseFloat((taxAmount || 0).toFixed(2));
                                                    const orderShipping = parseFloat((shippingCost || 0).toFixed(2));
                                                    const orderDiscount = parseFloat((discount || 0).toFixed(2));

                                                    // Calculate what PayPal expects
                                                    const calculatedTotal = orderSubtotal - orderDiscount + orderTax + orderShipping;

                                                    console.log('PayPal order details:', {
                                                        total: orderTotal,
                                                        subtotal: orderSubtotal,
                                                        tax: orderTax,
                                                        shipping: orderShipping,
                                                        discount: orderDiscount,
                                                        calculatedTotal: calculatedTotal,
                                                        difference: Math.abs(orderTotal - calculatedTotal),
                                                    });

                                                    // Ensure the total matches exactly
                                                    if (Math.abs(orderTotal - calculatedTotal) > 0.01) {
                                                        console.error('PayPal total mismatch!', {
                                                            orderTotal,
                                                            calculatedTotal,
                                                            difference: orderTotal - calculatedTotal,
                                                        });
                                                    }

                                                    const paypalOrder = {
                                                        intent: 'CAPTURE',
                                                        purchase_units: [
                                                            {
                                                                description: `Contractor Test LA: ${itemDescription}`,
                                                                amount: {
                                                                    currency_code: 'USD',
                                                                    value: orderTotal.toFixed(2),
                                                                    breakdown: {
                                                                        item_total: {
                                                                            currency_code: 'USD',
                                                                            value: orderSubtotal.toFixed(2),
                                                                        },
                                                                        tax_total: {
                                                                            currency_code: 'USD',
                                                                            value: orderTax.toFixed(2),
                                                                        },
                                                                        shipping: {
                                                                            currency_code: 'USD',
                                                                            value: orderShipping.toFixed(2),
                                                                        },
                                                                        discount:
                                                                            orderDiscount > 0
                                                                                ? {
                                                                                      currency_code: 'USD',
                                                                                      value: orderDiscount.toFixed(2),
                                                                                  }
                                                                                : undefined,
                                                                    },
                                                                },
                                                                items: cart.map((item) => ({
                                                                    name: item.name,
                                                                    unit_amount: {
                                                                        currency_code: 'USD',
                                                                        value: item.price.toFixed(2),
                                                                    },
                                                                    quantity: item.quantity.toString(),
                                                                    description: `6-month subscription included`,
                                                                    category: 'DIGITAL_GOODS',
                                                                })),
                                                            },
                                                        ],
                                                    };

                                                    console.log('Creating PayPal order:', paypalOrder);

                                                    return actions.order.create(paypalOrder);
                                                } catch (error) {
                                                    console.error('Error creating PayPal order:', error);
                                                    setError('Failed to create PayPal order. Please check your order details.');
                                                    throw error;
                                                }
                                            }}
                                            onApprove={(data, actions) => {
                                                setIsProcessingPayment(true);
                                                setError('');
                                                // Snapshot the checkout server-side BEFORE capturing, so the
                                                // reconcile cron can recover the order even if this browser
                                                // dies the moment after the money is taken.
                                                return snapshotPendingCheckout(data.orderID)
                                                    .then(() => actions.order.capture())
                                                    .then((details) => {
                                                        return createOrder(details);
                                                    })
                                                    .catch((err) => {
                                                        console.error('Payment processing error:', err);
                                                        setError('Payment processing failed. Please try again.');
                                                    })
                                                    .finally(() => {
                                                        setIsProcessingPayment(false);
                                                    });
                                            }}
                                            onError={(err) => {
                                                console.error('Error during PayPal transaction', err);
                                                setError('Payment failed. Please try again.');
                                            }}
                                            // Add these new handlers here
                                            onCancel={(data) => {
                                                console.log('Payment cancelled by user');
                                                setError('Payment was cancelled. Please try again when ready.');
                                            }}
                                            onClick={async (data, actions) => {
                                                // Validate any conditions before proceeding
                                                if (!validateForm()) {
                                                    setError('Please fill all required fields correctly.');
                                                    return actions.reject();
                                                }
                                                // Guest checkout: make sure the email isn't already tied to an
                                                // account. If it is, prompt login *before* PayPal captures —
                                                // otherwise we'd charge them and then fail at order creation.
                                                if (!user) {
                                                    try {
                                                        const checkRes = await axios.post(
                                                            `${process.env.REACT_APP_API_URL}/api/orders/check-email`,
                                                            { email: formData.email.trim() },
                                                        );
                                                        if (checkRes.data?.exists) {
                                                            setError('It looks like you already have an account. Please log in to complete your purchase — your cart will be preserved.');
                                                            setEmailExistsLogin(true);
                                                            setShowLoginRegister(true);
                                                            return actions.reject();
                                                        }
                                                    } catch (checkErr) {
                                                        // Non-fatal — if the pre-check fails we let PayPal proceed and the
                                                        // 409 fallback in createOrder() will catch it.
                                                        console.warn('Email pre-check failed:', checkErr);
                                                    }
                                                }
                                                return actions.resolve();
                                            }}
                                            style={{
                                                layout: 'vertical',
                                                height: 48, // Optional: control button height
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </PayPalScriptProvider>
                        ) : (
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleProceedToPayment}
                                sx={{
                                    backgroundColor: '#ff5e1c',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    py: 2,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    boxShadow: '0 6px 20px rgba(255, 94, 28, 0.3)',
                                    background: 'linear-gradient(135deg, #ff5e1c 0%, #ff7a3d 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #e54e0c 0%, #ff5e1c 100%)',
                                        boxShadow: '0 8px 25px rgba(255, 94, 28, 0.4)',
                                        transform: 'translateY(-2px)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Proceed to Payment
                            </Button>
                        )
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>
                            {!isFormComplete && (
                                <Typography color="error" sx={{ mb: 1 }}>
                                    Please fill in all required fields to proceed with payment.
                                </Typography>
                            )}
                            {isFormComplete && !guaranteeAcknowledged && (
                                <Typography color="warning.main" sx={{ fontWeight: 500 }}>
                                    Please acknowledge the No Pass, No Pay guarantee terms above to proceed with payment.
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Dialog
                        open={showLoginRegister}
                        onClose={() => {
                            setShowLoginRegister(false);
                            setEmailExistsLogin(false);
                        }}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            sx: {
                                borderRadius: 3,
                                boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                                background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
                            }
                        }}
                        BackdropProps={{
                            sx: {
                                backgroundColor: 'rgba(10, 18, 41, 0.75)',
                                backdropFilter: 'blur(4px)',
                            }
                        }}
                    >
                        <DialogContent sx={{ p: 0, '&:first-of-type': { paddingTop: 0 }, position: 'relative' }}>
                            <IconButton
                                onClick={() => {
                                    setShowLoginRegister(false);
                                    setEmailExistsLogin(false);
                                }}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: 16,
                                    color: '#666',
                                    zIndex: 1,
                                    backgroundColor: '#fff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        color: '#ff5e1c',
                                    }
                                }}
                            >
                                <CancelIcon />
                            </IconButton>
                            <LoginRegisterComponent
                                onLoginSuccess={handleLoginSuccess}
                                initialEmail={formData.email}
                                initialTab={emailExistsLogin ? 0 : 1}
                                // Add all other form data
                                initialData={{
                                    firstName: formData.firstName,
                                    lastName: formData.lastName,
                                    phone: formData.phoneNumber, // Note the field name difference
                                    address: formData.addressLine1, // Note the field name difference
                                    city: formData.city,
                                    state: formData.state,
                                    zip: formData.zip,
                                }}
                            />
                        </DialogContent>
                    </Dialog>
                    {error && (
                        <Typography color="error" align="center" sx={{ mt: 2 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </Container>
        </>
    );
};

export default Checkout;
